import React, { type MouseEventHandler } from 'react';

import { Button } from '@/components/design-system/Button';
import { Input } from '@/components/design-system/input';
import { IconWrapper } from '@/components/ui/IconWrapper';
import { cn } from '@/utils/cn';

type Status = 'default' | 'success' | 'error' | 'warning';

interface InputWithIconProps extends React.ComponentProps<typeof Input> {
  leftIcon?: IconComponent;
  rightIcon?: IconComponent;
  onRightIconClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  onLeftIconClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  rightIconTestId?: string;
  leftIconTestId?: string;
  rightIconClassName?: string;
  leftIconClassName?: string;
  inputClassName?: string;
  error?: boolean;
  status?: Status;
  width?: string;
}

type IconComponent = React.ComponentType<{ className?: string }>;

export const InputWithIcon: React.FC<InputWithIconProps> = ({
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onRightIconClick,
  onLeftIconClick,
  rightIconTestId,
  leftIconTestId,
  rightIconClassName,
  leftIconClassName,
  inputClassName,
  error,
  status = 'default',
  width = 'w-81',
  className,
  ...props
}) => {
  const iconBase = 'absolute top-1/2 text-lg transition-colors duration-300 -translate-y-1/2';

  const leftIconClass = cn(
    iconBase,
    'left-3.5',
    status === 'error'
      ? 'text-error-500'
      : status === 'success'
        ? 'text-success-500'
        : status === 'warning'
          ? 'text-warning-500'
          : 'text-neutral-950/45',
    leftIconClassName
  );

  const rightIconClass = cn(
    iconBase,
    'right-3.5 mt-px',
    status === 'error'
      ? 'text-error-500'
      : status === 'success'
        ? 'text-success-500'
        : status === 'warning'
          ? 'text-warning-500'
          : 'text-neutral-950/45'
  );

  const inputClass = cn(
    'text-neutral-950 placeholder:font-normal placeholder:text-neutral-400 border-neutral-300 w-full transition-colors duration-300',
    LeftIcon && 'pl-10',
    RightIcon && 'pr-10',
    status === 'error' &&
      'border-error-500 focus-visible:border-error-500 focus-visible:ring-error-500/20 hover:border-error-500',
    status === 'success' &&
      'border-success-500 focus-visible:border-success-500 focus-visible:ring-success-500/20 hover:border-success-400',
    status === 'warning' &&
      'border-warning-500 focus-visible:border-warning-500 focus-visible:ring-warning-500/20 hover:border-warning-500',
    inputClassName
  );

  const { 'data-testid': dataTestId, ...inputProps } = props as InputWithIconProps & { 'data-testid'?: string };

  return (
    <div className={cn('relative transition-all duration-300', width, className)}>
      {LeftIcon ? (
        onLeftIconClick ? (
          <Button
            type="button"
            variant="ghost"
            size="condensed"
            onClick={onLeftIconClick}
            data-testid={leftIconTestId}
            className={cn(leftIconClass, 'h-auto w-auto p-0 hover:bg-transparent')}
          >
            <IconWrapper icon={LeftIcon} />
          </Button>
        ) : (
          <IconWrapper icon={LeftIcon} className={leftIconClass} />
        )
      ) : null}

      <Input {...inputProps} data-testid={dataTestId} status={status} className={inputClass} />

      {RightIcon ? (
        onRightIconClick ? (
          <Button
            type="button"
            variant="ghost"
            size="condensed"
            onClick={onRightIconClick}
            data-testid={rightIconTestId}
            className={cn(rightIconClass, 'h-auto w-auto p-0 hover:bg-transparent', rightIconClassName)}
          >
            <IconWrapper icon={RightIcon} />
          </Button>
        ) : (
          <IconWrapper icon={RightIcon} className={rightIconClass} />
        )
      ) : null}
    </div>
  );
};

export default InputWithIcon;
